import { InputManager } from '../input/InputManager.js';
import { RenderView } from './RenderView.js';
import { CameraManager } from './camera/CameraManager.js';
import { update_from_partial } from './utility/utils.js';
export class View extends RenderView {
    constructor(viewer, interaction_system, render_width, render_height, framebuffer_type, wanted_msaa_samples) {
        super(viewer, interaction_system, render_width, render_height, framebuffer_type, wanted_msaa_samples);
        // The position of the view when blitted on the canvas
        this._canvas_position = { x: 0, y: 0 };
        // The size of the view when blitted on the canvas (NOT the resolution we render in, that's set on the framebuffer)
        this._canvas_blit_size = { x: 0, y: 0 };
        this._canvas_blit_size.x = render_width;
        this._canvas_blit_size.y = render_height;
        this.camera_manager = new CameraManager(this.engine_camera, interaction_system, this.viewer, render_width / render_height);
        this.input_manager = new InputManager(this, this.viewer, this.viewer.get_click_void_observable(), this.viewer.get_secondary_click_void_observable());
    }
    get_aspect_ratio() {
        return this._canvas_blit_size.x / this._canvas_blit_size.y;
    }
    update(delta_time) {
        if (!this.enabled)
            return;
        // Always update aspect ratio, in case a camera is shared between Views.
        this.camera_manager.update_aspect_ratio(this.get_aspect_ratio());
        this.camera_manager.update(delta_time);
        super.update(delta_time);
        // An optimization would be for this function to only be evaluated if our view is dirty (i.e. in the render() function),
        // however we can't know in here if our view is dirty due to not knowing yet if the scene will dirty us.
        // This function may also dirty the view or scene, and thus it's not enough to evaluate only when rendering.
        // Thus, we always update it every frame.
        this.input_manager.update_pointer_enter_leave();
    }
    get_canvas_viewport() {
        return {
            x: this._canvas_position.x,
            y: this._canvas_position.y,
            width: this._canvas_blit_size.x,
            height: this._canvas_blit_size.y
        };
    }
    set_canvas_viewport(partial_viewport) {
        const current_viewport = this.get_canvas_viewport();
        const viewport = update_from_partial(current_viewport, partial_viewport);
        viewport.width = Math.floor(viewport.width);
        viewport.height = Math.floor(viewport.height);
        viewport.x = Math.floor(viewport.x);
        viewport.y = Math.floor(viewport.y);
        // Early out if nothing changed.
        if (viewport.x === current_viewport.x &&
            viewport.y === current_viewport.y &&
            viewport.width === current_viewport.width &&
            viewport.height === current_viewport.height) {
            return;
        }
        this._canvas_position.x = viewport.x;
        this._canvas_position.y = viewport.y;
        this._canvas_blit_size.x = viewport.width;
        this._canvas_blit_size.y = viewport.height;
        // We use the blit size instead of the render size
        // This is because if you want to animate the size of a view, you only want to animate the blit size.
        this.camera_manager.update_aspect_ratio(viewport.width / viewport.height);
        this.viewport_changed_observable.notify(this, viewport);
        this.viewer.mark_composite_dirty();
        this.viewer.mark_backbuffer_clear_dirty();
    }
    project_world_position_to_canvas(world_position) {
        const ndc = this.project_world_position_to_ndc(world_position);
        // The position is outside the projection
        if (Math.abs(ndc.x) > 1.0 || Math.abs(ndc.y) > 1.0 || Math.abs(ndc.z) > 1.0) {
            return null;
        }
        const canvas_position = {
            x: this._canvas_position.x + this._canvas_blit_size.x * (ndc.x * 0.5 + 0.5),
            y: this._canvas_position.y + this._canvas_blit_size.y * (1.0 - (ndc.y * 0.5 + 0.5))
        };
        return canvas_position;
    }
}
//# sourceMappingURL=View.js.map